'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import {
  ManifestoChapter,
  PageManifestoSchema,
} from '@/sanity/pages/PageManifestoTemplate';
import MissionBannerSection from './MissionBannerSection';
import Image from 'next/image';
import dotsBackground from '@/public/manifesto/dots-background.png';

const ease = [0.25, 0.1, 0.25, 1] as const;

function ChapterQuote({ text }: { text: string }) {
  return (
    <div className="flex gap-[1.5rem] bg-black/[0.04] px-[2.5rem] py-[2rem] backdrop-blur-[1.625rem] border-l-[.125rem] border-black">
      <svg
        className="w-[2rem] h-[2rem] shrink-0 lg:mt-[0.5rem]"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="none"
        viewBox="0 0 16 16"
      >
        <path
          fill="#000"
          d="m9.408 15.064 2.688-8.736H9.24V0h6.328v5.544l-3.36 9.52zm-9.24 0 2.688-8.736H0V0h6.328v5.544l-3.36 9.52z"
          opacity="0.8"
        ></path>
      </svg>
      <p className="mb-0 text-[2rem] text-black/80 leading-[1.5]">{text}</p>
    </div>
  );
}

function Chapter({
  chapter,
  index,
  isLast,
  data,
}: {
  chapter: ManifestoChapter;
  index: number;
  isLast?: boolean;
  data: PageManifestoSchema;
}) {
  return (
    <div className="relative border border-black/10 rounded-[0.25rem] overflow-hidden">
      {/* Top area: heading + image */}
      <div className="flex items-center min-h-[24rem] py-[4rem] justify-between gap-[3rem] px-[4rem] relative lg:min-h-[42rem] lg:gap-[4rem] lg:px-[8rem] ">
        {/* Dots bg must be outside any element with transform/opacity to keep mix-blend-mode working */}
        <div className="absolute mix-blend-difference opacity-40 w-full h-full inset-0 z-0 pointer-events-none select-none">
          <Image className="" alt="Background" fill src={dotsBackground} />
        </div>
        {/* Accent bar */}
        {chapter.accentColor && (
          <div
            className="absolute left-0 top-0 h-full w-[0.375rem]"
            style={{ backgroundColor: chapter.accentColor }}
          />
        )}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.9, ease, delay: 0.05 * index }}
          className="flex flex-col gap-[2rem] max-w-full min-h-[20rem] lg:max-w-[44rem] lg:min-h-[30rem] lg:gap-[2.5rem] relative z-10"
        >
          {chapter.heading && (
            <p className="mb-0 font-semibold text-black tracking-[-0.09rem] leading-[1.08] text-[4rem] lg:tracking-[-0.12rem] lg:leading-[1.01]">
              {chapter.heading}
            </p>
          )}
          {chapter.intro && (
            <div className="richtext richtext-start flex flex-col gap-[1.5rem] mt-auto prose-p:text-black/70 prose-p:tracking-[-0.08rem]">
              <PortableText
                value={chapter.intro}
                components={richTextComponent}
              />
            </div>
          )}
          {chapter.quote && <ChapterQuote text={chapter.quote} />}
        </motion.div>

        {chapter.image && (
          <div className="w-1/2 lg:block hidden h-full absolute right-[0rem] bottom-0">
            <GroqImage
              src={chapter.image}
              alt={chapter.heading || ''}
              className="!w-full !h-full !object-contain"
            />
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-black/10" />

      {/* Body */}
      {chapter.body && (
        <div
          className={`${isLast ? 'pb-[0rem] px-[4rem] pt-[4rem] lg:pb-[4rem] lg:px-[8rem] lg:pt-[8rem]' : 'p-[4rem] lg:p-[8rem]'} richtext richtext-start flex flex-col gap-[2.5rem] prose-strong:text-[2.5rem] prose-headings:text-[2.5rem] prose-headings:font-medium prose-headings:tracking-[-0.15rem] prose-headings:leading-[1.12] lg:gap-[3rem] lg:prose-strong:text-[3rem] lg:prose-headings:text-[3rem]`}
        >
          <PortableText value={chapter.body} components={richTextComponent} />
        </div>
      )}

      {isLast && <MissionBannerSection data={data} />}
    </div>
  );
}

export default function ChaptersSection({
  data,
}: {
  data: PageManifestoSchema;
}) {
  if (!data?.manifestoChapters?.length) return null;

  return (
    <section className="relative w-full flex flex-col items-center  lg:py-[4rem] bg-white">
      <div className="lg:w-[min(94.5rem,calc(100%-8rem))] w-[min(94.5rem,calc(100%-4rem))] mx-auto flex flex-col gap-[4rem]">
        {data.manifestoChapters.map((chapter, i) => (
          <Chapter
            key={chapter._key || i}
            chapter={chapter}
            index={i}
            isLast={i === data.manifestoChapters!.length - 1}
            data={data}
          />
        ))}
      </div>
    </section>
  );
}
